import { type ReactNode } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/theme';
import { useSelectFieldInsideModal } from '@/components/ui/SelectFieldModalContext';

type InModalOverlayProps = {
  visible: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Bottom sheet (default) or centered dialog */
  variant?: 'bottom' | 'center';
  /** Override backdrop tap; defaults to onClose */
  onBackdropPress?: () => void;
};

/**
 * iOS RN Modal stacks above PortalHost — portaled pickers render behind the parent sheet.
 * Render this overlay as a sibling inside the same Modal (absolute fill + high zIndex).
 */
export function InModalOverlay({
  visible,
  onClose,
  children,
  variant = 'bottom',
  onBackdropPress,
}: InModalOverlayProps) {
  const theme = useTheme();

  if (!visible) {
    return null;
  }

  const isCenter = variant === 'center';

  return (
    <View style={styles.host} pointerEvents="box-none">
      <Pressable
        style={[
          styles.backdrop,
          {
            backgroundColor: theme.colors.overlay,
            justifyContent: isCenter ? 'center' : 'flex-end',
            alignItems: isCenter ? 'center' : 'stretch',
            padding: isCenter ? 24 : 0,
          },
        ]}
        onPress={onBackdropPress ?? onClose}
        accessibilityRole="button"
        accessibilityLabel="Close picker"
      >
        <Pressable onPress={(e) => e.stopPropagation()} style={{ width: '100%' }}>
          {children}
        </Pressable>
      </Pressable>
    </View>
  );
}

/** Use in-modal absolute overlay when already inside an RN Modal (iOS only; web uses inline/portal pickers). */
export function useIosInModalOverlay(): boolean {
  const isInsideModal = useSelectFieldInsideModal();
  return isInsideModal && Platform.OS === 'ios';
}

const styles = StyleSheet.create({
  host: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
    elevation: 9999,
  },
  backdrop: {
    flex: 1,
  },
});
